import React from 'react'
import { Box, Checkbox, Field, Flex, Input, Text } from '@workwhile/ui'
import { FieldError, FieldErrors } from 'react-hook-form'
import {
  UserPermission,
  UserPermissionOption,
} from 'api/admin/userPermissions/permissions'
import { PermissionGrantFormSchema, UserPermissionsFormSchema } from '../schema'
import { format } from 'date-fns'

export function getDefaultEffectiveAt(): string {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm")
}

type PermissionsChecklistProps = {
  options: UserPermissionOption[]
  value: PermissionGrantFormSchema[]
  onChange: (permissions: PermissionGrantFormSchema[]) => void
  disabledPermissions?: UserPermission[]
  errors?: FieldErrors<UserPermissionsFormSchema>['permissions']
}

export const PermissionsChecklist = ({
  options,
  value,
  onChange,
  disabledPermissions = [],
  errors,
}: PermissionsChecklistProps) => {
  const disabledSet = new Set(disabledPermissions)

  const handleToggle = (permission: UserPermission, checked: boolean) => {
    if (disabledSet.has(permission)) {
      return
    }
    if (checked) {
      if (value.some((item) => item.permission === permission)) {
        return
      }
      onChange([
        ...value,
        {
          permission,
          effectiveAt: getDefaultEffectiveAt(),
          expiresAt: null,
        },
      ])
      return
    }
    onChange(value.filter((item) => item.permission !== permission))
  }

  const handleDateChange = (
    permission: UserPermission,
    field: 'effectiveAt' | 'expiresAt',
    raw: string
  ) => {
    onChange(
      value.map((item) => {
        if (item.permission !== permission) {
          return item
        }
        if (field === 'expiresAt') {
          return { ...item, expiresAt: raw === '' ? null : raw }
        }
        return { ...item, effectiveAt: raw }
      })
    )
  }

  const handleNeverExpiresChange = (
    permission: UserPermission,
    neverExpires: boolean
  ) => {
    onChange(
      value.map((item) => {
        if (item.permission !== permission) {
          return item
        }
        if (neverExpires) {
          return { ...item, expiresAt: null }
        }
        return {
          ...item,
          expiresAt: item.effectiveAt || getDefaultEffectiveAt(),
        }
      })
    )
  }

  return (
    <Box>
      <Text fontWeight="medium" mb={2}>
        Permissions
      </Text>
      <Flex flexDirection="column" gap={2}>
        {options.map((option) => {
          const selected = value.find(
            (item) => item.permission === option.value
          )
          const selectedIndex = value.findIndex(
            (item) => item.permission === option.value
          )
          const itemErrors =
            selectedIndex >= 0 && Array.isArray(errors)
              ? errors[selectedIndex]
              : undefined
          const effectiveAtError = (
            itemErrors?.effectiveAt as FieldError | undefined
          )?.message?.toString()
          const expiresAtError = (
            itemErrors?.expiresAt as FieldError | undefined
          )?.message?.toString()
          const isDisabled = disabledSet.has(option.value)
          const isChecked = Boolean(selected) || isDisabled
          const neverExpires = selected?.expiresAt == null

          return (
            <Box
              key={option.value}
              p={2}
              border="1px solid"
              borderColor="neutrals.100"
              borderRadius="small"
              opacity={isDisabled ? 0.7 : 1}
            >
              <Checkbox
                value={isChecked}
                disabled={isDisabled}
                onChange={(checked) => handleToggle(option.value, checked)}
              >
                <Box>
                  <Text fontWeight="medium">{option.name}</Text>
                  <Text color="lightText" fontSize={1}>
                    {isDisabled
                      ? 'Already granted for this user'
                      : option.description}
                  </Text>
                </Box>
              </Checkbox>

              {selected ? (
                <Flex mt={3} gap={3} flexDirection={['column', 'row']}>
                  <Box flex={1}>
                    <Field label="Effective at" error={effectiveAtError}>
                      <Input
                        type="datetime-local"
                        step={60}
                        value={selected.effectiveAt}
                        onChange={(event) =>
                          handleDateChange(
                            option.value,
                            'effectiveAt',
                            event.target.value
                          )
                        }
                        aria-label={`${option.name} effective at`}
                      />
                    </Field>
                  </Box>
                  <Box flex={1}>
                    <Field label="Expires at" error={expiresAtError}>
                      <Flex flexDirection="column" gap={2}>
                        <Input
                          type="datetime-local"
                          step={60}
                          value={selected.expiresAt ?? ''}
                          disabled={neverExpires}
                          onChange={(event) =>
                            handleDateChange(
                              option.value,
                              'expiresAt',
                              event.target.value
                            )
                          }
                          aria-label={`${option.name} expires at`}
                        />
                        <Checkbox
                          value={neverExpires}
                          onChange={(checked) =>
                            handleNeverExpiresChange(option.value, checked)
                          }
                        >
                          Never expires
                        </Checkbox>
                      </Flex>
                    </Field>
                  </Box>
                </Flex>
              ) : null}
            </Box>
          )
        })}
      </Flex>
    </Box>
  )
}
